import {
  ChannelType,
  ConversionStageReachedType,
  DeviceType,
  PurchaseLikelihoodType,
  VisitorType,
} from "@intelligems/ig-types";

export const deviceTypeList: { label: string; key: DeviceType }[] = [
  {
    label: "All Devices",
    key: "any",
  },
  {
    label: "Desktop",
    key: "desktop",
  },
  {
    label: "Mobile",
    key: "mobile",
  },
];

export const conversionStageReachedList: {
  label: string;
  key: ConversionStageReachedType;
}[] = [
  {
    label: "All Stages",
    key: "any",
  },
  {
    label: "Added to Cart",
    key: "added_to_cart",
  },
  {
    label: "Started Checkout",
    key: "started",
  },
  {
    label: "Contact Info Submitted",
    key: "contact_info_submitted",
  },
  {
    label: "Address Info Submitted",
    key: "address_info_submitted",
  },
];

export const getConversionStageReachedList = ({
  hasCheckoutBlocks = false,
  hasOnsiteUpsell = false,
  hasTestPostPurchase = false,
}: {
  hasCheckoutBlocks?: boolean;
  hasOnsiteUpsell?: boolean;
  hasTestPostPurchase?: boolean;
} = {}): {
  label: string;
  key: ConversionStageReachedType;
}[] => {
  if (!hasCheckoutBlocks && !hasOnsiteUpsell && !hasTestPostPurchase) {
    return conversionStageReachedList;
  }

  // "Viewed Checkout Block" sits directly after "Started Checkout" — i.e. above
  // "Contact Info Submitted" — so the list still reads in funnel order.
  const stages = conversionStageReachedList.flatMap((stage) => {
    if (stage.key !== "started") {
      return [stage];
    }

    return [
      stage,
      ...(hasCheckoutBlocks
        ? [
            {
              label: "Viewed Checkout Block",
              key: "checkout_block_viewed" as const,
            },
          ]
        : []),
      ...(hasOnsiteUpsell
        ? [
            {
              label: "Viewed Onsite Upsell",
              key: "onsite_upsell_viewed" as const,
            },
          ]
        : []),
    ];
  });

  if (hasTestPostPurchase) {
    stages.push({
      label: "Viewed Post Purchase",
      key: "post_purchase_offer_viewed",
    });
  }

  return stages;
};

export const visitorTypeList: {
  label: string;
  key: VisitorType | undefined;
  description?: string;
}[] = [
  {
    label: "All Visitors",
    key: "any",
    description: "Include all visitors",
  },
  {
    label: "New",
    key: "new",
    description:
      "Include only visitors with no prior browsing session tracked by Intelligems",
  },
  {
    label: "Returning",
    key: "returning",
    description:
      "Include only visitors with a previously tracked browsing session",
  },
];

export const purchaseLikelihoodList: {
  label: string;
  key: PurchaseLikelihoodType | null;
  // Share of scored traffic each bucket holds, shown under the bucket label.
  description?: string;
}[] = [
  {
    label: "All Likelihoods",
    key: null,
  },
  {
    label: "Higher",
    key: "higher",
    description: "Top 20% of traffic",
  },
  {
    label: "Medium",
    key: "medium",
    description: "Middle 50% of traffic",
  },
  {
    label: "Lower",
    key: "lower",
    description: "Bottom 30% of traffic",
  },
];

export const trafficSourceList: { label: string; key: ChannelType }[] = [
  {
    label: "Google",
    key: "Google",
  },
  {
    label: "Facebook",
    key: "Facebook",
  },
  {
    label: "Instagram",
    key: "Instagram",
  },
  {
    label: "TikTok",
    key: "TikTok",
  },
  {
    label: "Twitter",
    key: "Twitter",
  },
  {
    label: "YouTube",
    key: "Youtube",
  },
];

export const newChannelList: { label: string; key: ChannelType }[] = [
  {
    label: "Direct",
    key: "Direct",
  },
  {
    label: "Email",
    key: "Email",
  },
  {
    label: "Referral",
    key: "Referral",
  },
  {
    label: "Affiliate",
    key: "Affiliate",
  },
  {
    label: "Organic Social",
    key: "Organic Social",
  },
  {
    label: "Paid Social",
    key: "Paid Social",
  },
  {
    label: "Organic Search",
    key: "Organic Search",
  },
  {
    label: "Paid Search",
    key: "Paid Search",
  },
  {
    label: "Paid Shopping",
    key: "Paid Shopping",
  },
  {
    label: "SMS",
    key: "SMS",
  },
];

export const channelTypeList: { label: string; key: ChannelType | null }[] = [
  {
    label: "All Sources",
    key: null,
  },
  ...newChannelList,
  ...trafficSourceList,
];
